import React, {useState, useEffect, useRef, useCallback} from "react";
import {useParams, Link} from "react-router-dom";
import "./LiveJoinPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const WS_BASE  = process.env.REACT_APP_WS_BASE  || "";
const JAAS_APP_ID = process.env.REACT_APP_JAAS_APP_ID || "";
const JITSI_HOST = "meet.jit.si";

const EARLY_WINDOW_MINUTES = 15;

function getJoinStatus(scheduledAt) {
    if (!scheduledAt) return "loading";
    const now = new Date();
    const scheduled = new Date(scheduledAt);
    const diffMs = scheduled - now;
    const diffMins = diffMs / 60000;

    if (diffMins > EARLY_WINDOW_MINUTES) return "too_early";
    if (diffMins > -60) return "ready";
    return "expired";
}

function formatCountdown(scheduledAt) {
    const diff = new Date(scheduledAt) - new Date();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
}

function JitsiCall({roomName, displayName, onEnd}) {
    const containerRef = useRef(null);
    // Latest onEnd without restarting the call when the parent re-renders.
    const onEndRef = useRef(onEnd);
    onEndRef.current = onEnd;
    const apiRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            if (!containerRef.current) return;

            let jwt = null;
            if (JAAS_APP_ID) {
                try {
                    const r = await fetch(`${API_BASE}/api/live-tour/jaas-token`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({userName: displayName, roomName, moderator: "false"}),
                    });
                    if (r.ok) jwt = (await r.json()).token;
                } catch { /* non-critical */
                }
            }

            const domain = JAAS_APP_ID ? "8x8.vc" : JITSI_HOST;
            const jaasRoom = JAAS_APP_ID ? `${JAAS_APP_ID}/${roomName}` : roomName;

            const initApi = () => {
                if (!containerRef.current || !window.JitsiMeetExternalAPI) return;
                const opts = {
                    roomName: jaasRoom,
                    parentNode: containerRef.current,
                    width: "100%",
                    height: "100%",
                    userInfo: {displayName: displayName || "Customer"},
                    configOverwrite: {
                        startWithAudioMuted: false,
                        startWithVideoMuted: false,
                        prejoinPageEnabled: false,
                        prejoinConfig: {enabled: false},
                        disableDeepLinking: true,
                        p2p: {enabled: false},
                    },
                    interfaceConfigOverwrite: {
                        SHOW_JITSI_WATERMARK: false,
                        SHOW_BRAND_WATERMARK: false,
                        SHOW_POWERED_BY: false,
                        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                        TOOLBAR_ALWAYS_VISIBLE: true,
                        MOBILE_APP_PROMO: false,
                    },
                };
                if (jwt) opts.jwt = jwt;

                apiRef.current = new window.JitsiMeetExternalAPI(domain, opts);
                apiRef.current.addEventListeners({
                    readyToClose: () => onEndRef.current?.(),
                    participantLeft: () => {
                        const count = apiRef.current?.getNumberOfParticipants?.() || 0;
                        if (count <= 1) setTimeout(() => onEndRef.current?.(), 3000);
                    },
                });
            };

            const scriptSrc = JAAS_APP_ID
                ? `https://8x8.vc/${JAAS_APP_ID}/external_api.js`
                : `https://${JITSI_HOST}/external_api.js`;

            if (window.JitsiMeetExternalAPI) {
                initApi();
            } else {
                const s = document.createElement("script");
                s.src = scriptSrc;
                s.async = true;
                s.onload = initApi;
                document.body.appendChild(s);
            }
        };

        load();
        return () => {
            apiRef.current?.dispose();
        };
    }, [roomName, displayName]);

    return <div ref={containerRef} style={{width: "100%", height: "100%"}}/>;
}

/**
 * Live tour join page reached from a customer's booking link. Shows the
 * scheduled slot, gates the "Join" action to a window around the scheduled
 * time, and connects the customer into the Jitsi/JaaS video room once an
 * agent accepts the request (via WebSocket push with HTTP-poll fallback).
 */
export default function LiveJoinPage() {
    const {requestId} = useParams();

    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [joinStatus, setJoinStatus] = useState("loading");
    const [countdown, setCountdown] = useState(null);
    const [callActive, setCallActive] = useState(false);
    const [callEnded, setCallEnded] = useState(false);
    const [nameInput, setNameInput] = useState("");
    const [nameSet, setNameSet] = useState(false);

    useEffect(() => {
        if (!requestId) return;
        fetch(`${API_BASE}/api/live-tour/request-status/${requestId}`)
            .then(r => r.ok ? r.json() : Promise.reject("not_found"))
            .then(data => {
                setRequest(data);
                setJoinStatus(getJoinStatus(data.scheduledAt));
                setNameInput(data.fullName || "");
            })
            .catch(() => setError("This tour link is invalid or has expired."))
            .finally(() => setLoading(false));
    }, [requestId]);

    useEffect(() => {
        if (!request?.scheduledAt) return;
        const tick = () => {
            const status = getJoinStatus(request.scheduledAt);
            setJoinStatus(status);
            setCountdown(formatCountdown(request.scheduledAt));
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [request?.scheduledAt]);

    // Plain WebSocket -- the backend's /live-queue endpoint speaks a JSON
    // {"action","topic"} envelope (app/modules/websocket/router.py), not
    // STOMP framing, so a SockJS+stomp.js client here (as this used to be)
    // never actually connects: its STOMP CONNECT frame isn't valid JSON, so
    // the backend's `receive_json()` throws and drops the socket before any
    // subscribe goes through. The 2s HTTP poll below was already covering
    // for that silently; the WS push is now a real (not just intended) fast
    // path, with the poll left in place as the fallback it was designed to be.
    useEffect(() => {
        if (joinStatus !== "ready" || !requestId || callActive) return;

        let mounted = true;
        let ws      = null;

        const handleAgentJoined = (d) => {
            if (!mounted) return;
            mounted = false;
            try { ws?.close(); } catch {}
            setRequest(prev => ({ ...prev, ...d }));
            setCallActive(true);
        };

        const poll = setInterval(async () => {
            try {
                const r = await fetch(`${API_BASE}/api/live-tour/request-status/${requestId}`);
                const d = await r.json();
                if (d.status === "AGENT_JOINED") {
                    clearInterval(poll);
                    handleAgentJoined(d);
                }
            } catch { /* keep polling */ }
        }, 2000);

        const roomName = request?.roomName;
        if (roomName) {
            try {
                const url = new URL("/live-queue", WS_BASE || API_BASE || window.location.origin);
                if (!WS_BASE) url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
                ws = new WebSocket(url.toString());

                ws.onopen = () => {
                    ws.send(JSON.stringify({ action: "subscribe", topic: `customer/${roomName}/call-accepted` }));
                };

                ws.onmessage = (event) => {
                    try {
                        const d = JSON.parse(event.data);
                        if (d.accepted === true || d.type === "AGENT_JOINED") {
                            clearInterval(poll);
                            handleAgentJoined({ ...d, status: "AGENT_JOINED" });
                        }
                    } catch {}
                };
            } catch { /* WS unavailable — poll covers it */ }
        }

        return () => {
            mounted = false;
            clearInterval(poll);
            try { ws?.close(); } catch {}
        };
    }, [joinStatus, requestId, callActive, request?.roomName]);

    const handleJoin = useCallback(() => {
        if (!nameSet && !nameInput.trim()) return;
        setCallActive(true);
    }, [nameSet, nameInput]);

    const handleCallEnd = useCallback(() => {
        setCallActive(false);
        setCallEnded(true);
    }, []);

    if (callActive && request?.roomName) {
        return (
            <div className="ljp-call-screen">
                <div className="ljp-call-bar">
                    <div className="ljp-call-brand">
                        <div className="ljp-brand-icon">OGM</div>
                        <div>
                            <span className="ljp-brand-name">OGM Live</span>
                            <span className="ljp-brand-sub">Live Property Tour</span>
                        </div>
                    </div>
                    <div className="ljp-call-live">
                        <span className="ljp-live-dot"/>
                        Live
                    </div>
                    <button className="ljp-end-btn" onClick={handleCallEnd}>
                        End Tour
                    </button>
                </div>
                <div className="ljp-jitsi-wrap">
                    <JitsiCall
                        roomName={request.roomName}
                        displayName={nameInput || request.fullName || "Customer"}
                        onEnd={handleCallEnd}
                    />
                </div>
            </div>
        );
    }

    if (callEnded) {
        return (
            <div className="ljp-page">
                <div className="ljp-card ljp-thankyou">
                    <div className="ljp-ty-icon">🏡</div>
                    <h2 className="ljp-ty-title">Thank you!</h2>
                    <p className="ljp-ty-sub">
                        Hope you enjoyed the tour of <strong>{request?.propertyTitle}</strong>.
                        Our advisor will follow up with you shortly.
                    </p>
                    <Link to="/" className="ljp-btn ljp-btn--primary">← Back to Listings</Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="ljp-page ljp-page--center">
                <div className="ljp-spinner"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ljp-page ljp-page--center">
                <div className="ljp-card ljp-card--narrow" style={{textAlign: "center"}}>
                    <div style={{fontSize: 48, marginBottom: 16}}>🔗</div>
                    <h2 className="ljp-section-title">{error}</h2>
                    <Link to="/" className="ljp-btn ljp-btn--primary" style={{marginTop: 20, display: "inline-block"}}>
                        Back to listings
                    </Link>
                </div>
            </div>
        );
    }

    const formattedTime = request?.scheduledAt
        ? new Date(request.scheduledAt).toLocaleString("en-IN", {
            weekday: "short", day: "numeric", month: "short",
            year: "numeric", hour: "2-digit", minute: "2-digit",
        })
        : "";

    return (
        <div className="ljp-page">
            <nav className="ljp-nav">
                <Link to="/" className="ljp-nav-logo">
                    <div className="ljp-brand-icon">OG</div>
                    <span>One Global Marketplace</span>
                </Link>
            </nav>

            <div className="ljp-content">
                <div className="ljp-card ljp-prop-card">
                    {request?.propertyImage && (
                        <img
                            src={request.propertyImage}
                            alt={request.propertyTitle}
                            className="ljp-prop-img"
                        />
                    )}
                    <div className="ljp-prop-info">
                        <p className="ljp-prop-label">You're joining a live tour for</p>
                        <h1 className="ljp-prop-title">{request?.propertyTitle || "Property Tour"}</h1>
                        {request?.propertyLocation && (
                            <p className="ljp-prop-location">📍 {request.propertyLocation}</p>
                        )}
                    </div>
                </div>

                <div className="ljp-card">
                    <div className="ljp-booking-grid">
                        <div className="ljp-booking-item">
                            <span className="ljp-booking-label">Scheduled</span>
                            <span className="ljp-booking-value">{formattedTime}</span>
                        </div>
                        <div className="ljp-booking-item">
                            <span className="ljp-booking-label">Name</span>
                            <span className="ljp-booking-value">{request?.fullName}</span>
                        </div>
                        {request?.whatsappNumber && (
                            <div className="ljp-booking-item">
                                <span className="ljp-booking-label">WhatsApp</span>
                                <span className="ljp-booking-value">+91 {request.whatsappNumber}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="ljp-card ljp-join-card">

                    {joinStatus === "too_early" && (
                        <div className="ljp-status ljp-status--early">
                            <div className="ljp-countdown-label">Your tour starts in</div>
                            <div className="ljp-countdown">{countdown}</div>
                            <p className="ljp-status-note">
                                The join button will appear {EARLY_WINDOW_MINUTES} minutes before your tour.
                            </p>
                            <div className="ljp-divider"/>
                            <p className="ljp-how-title">How to join</p>
                            <div className="ljp-steps">
                                {[
                                    "Click the join button when it appears",
                                    "Allow camera and microphone access",
                                    "Our advisor will join and walk you through the property",
                                ].map((s, i) => (
                                    <div key={i} className="ljp-step">
                                        <div className="ljp-step-num">{i + 1}</div>
                                        <span>{s}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {joinStatus === "ready" && (
                        <div className="ljp-status ljp-status--ready">
                            <div className="ljp-ready-dot"/>
                            <h2 className="ljp-ready-title">Your tour is ready to start</h2>
                            <p className="ljp-ready-sub">
                                Our advisor is standing by. Enter your name and join now.
                            </p>

                            {!nameSet ? (
                                <div className="ljp-name-row">
                                    <input
                                        type="text"
                                        className="ljp-input"
                                        placeholder="Your name"
                                        value={nameInput}
                                        onChange={e => setNameInput(e.target.value)}
                                        onKeyDown={e => e.key === "Enter" && nameInput.trim() && setNameSet(true)}
                                        autoFocus
                                    />
                                    <button
                                        className="ljp-btn ljp-btn--primary"
                                        onClick={() => nameInput.trim() && setNameSet(true)}
                                        disabled={!nameInput.trim()}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="ljp-btn ljp-btn--join"
                                    onClick={handleJoin}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="23 7 16 12 23 17 23 7"/>
                                        <rect x="1" y="5" width="15" height="14" rx="2"/>
                                    </svg>
                                    Join Live Tour
                                </button>
                            )}
                        </div>
                    )}

                    {joinStatus === "expired" && (
                        <div className="ljp-status ljp-status--expired">
                            <div style={{fontSize: 40, marginBottom: 12}}>⏰</div>
                            <h2 className="ljp-section-title">This tour link has expired</h2>
                            <p className="ljp-status-note" style={{marginBottom: 20}}>
                                The scheduled time has passed. Please book a new slot.
                            </p>
                            <Link
                                to={`/property/${request?.propertySlug || ""}`}
                                className="ljp-btn ljp-btn--primary"
                            >
                                Book a new slot
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
